<template>
  <div>
    <div v-if="isLoading">Loading reports data...</div>

    <div v-else-if="error" class="error-container d-flex justify-center gap5">
      <div class="error-icon" aria-label="Error">&#9888;</div>
      <div class="error-message">
        <strong>Report is not ready.</strong>
      </div>
    </div>

    <div v-else class="container">
      <h1>Existing Page Update Brief - Top Level Page</h1>

      <!-- SECTION 1: Main Keyword Updates -->
      <div v-if="hasKeywordUpdates" class="report-section">
        <div class="section-header">SECTION 1</div>
        <h2 class="section-title">Main Keyword Updates</h2>

        <!-- Review and update main keywords -->
        <div
          v-if="keywordSections.reviewMainKeywords.length > 0"
          class="keyword-subsection"
        >
          <h2 class="sub-section-title">
            {{ getKeywordSubsectionNumber('reviewMainKeywords') }}.
            Review and update main keywords
          </h2>
          <result7-paragraph
            :isWhiteGloveUser="false"
            :isMissionControlSession="false"
            :isDemo="false"
            :isLoading="false"
            :pageRun="null"
            :pAIResult="keywordSections.reviewMainKeywords"
            :calculated_titleAIResult="null"
            :editorDisplayByDEfault="true"
            :calculated_pageTitleAIResult="null"
            :editingParagraphIdxInResult.sync="currentEditingIdx"
            :handleParagraphEditDebounced="handleKeywordEdit"
            :handleRemoveContent="handleRemoveKeyword"
            :regenerateForParagraphOneSection="regenerateForParagraphOneSection"
            :seletedParagraphSection.sync="selectedParagraphSection"
            :showParagraphOneSectionHistoryModal.sync="showParagraphOneSectionHistoryModal"
            :textToCssId="textToCssId"
            :setNeedsToSave="setNeedsToSave"
            :getAllContentPromptDataForPSection="getAllContentPromptDataForPSection"
            :aiGeneratedImagesMap="{}"
            :imageAIModalParams.sync="imageAIModalParams"
            :deleteImageAIModalParams.sync="deleteImageAIModalParams"
            :showContnentPromptGenerationModal="showContnentPromptGenerationModal"
            :showEditPopupForRichItem="showEditPopupForRichItem"
            :showHistoryForRichItem="showHistoryForRichItem"
            :highlightKeywords="highlightKeywords"
            @clickCheckOptimizationScore="onCheckOptimizationScore"
            @startOver="onStartOver"
            :displayHead="false"
            :showTags="false"
            :saveParentChanges="saveChanges"
          />
        </div>

        <!-- Incorporate new related keywords -->
        <div
          v-if="keywordSections.incorporateKeywords.length > 0"
          class="keyword-subsection"
        >
          <h2 class="sub-section-title">
            {{ getKeywordSubsectionNumber('incorporateKeywords') }}.
            Incorporate new related keywords
          </h2>
          <result7-paragraph
            :isWhiteGloveUser="false"
            :isMissionControlSession="false"
            :isDemo="false"
            :isLoading="false"
            :pageRun="null"
            :pAIResult="keywordSections.incorporateKeywords"
            :calculated_titleAIResult="null"
            :editorDisplayByDEfault="true"
            :calculated_pageTitleAIResult="null"
            :editingParagraphIdxInResult.sync="currentEditingIdx"
            :handleParagraphEditDebounced="handleKeywordEdit"
            :handleRemoveContent="handleRemoveKeyword"
            :regenerateForParagraphOneSection="regenerateForParagraphOneSection"
            :seletedParagraphSection.sync="selectedParagraphSection"
            :showParagraphOneSectionHistoryModal.sync="showParagraphOneSectionHistoryModal"
            :textToCssId="textToCssId"
            :setNeedsToSave="setNeedsToSave"
            :getAllContentPromptDataForPSection="getAllContentPromptDataForPSection"
            :aiGeneratedImagesMap="{}"
            :imageAIModalParams.sync="imageAIModalParams"
            :deleteImageAIModalParams.sync="deleteImageAIModalParams"
            :showContnentPromptGenerationModal="showContnentPromptGenerationModal"
            :showEditPopupForRichItem="showEditPopupForRichItem"
            :showHistoryForRichItem="showHistoryForRichItem"
            :highlightKeywords="highlightKeywords"
            @clickCheckOptimizationScore="onCheckOptimizationScore"
            @startOver="onStartOver"
            :displayHead="false"
            :showTags="false"
            :saveParentChanges="saveChanges"
          />
        </div>

        <!-- Optimize subheadings -->
        <div
          v-if="keywordSections.optimizeSubheadings.length > 0"
          class="keyword-subsection"
        >
          <h2 class="sub-section-title">
            {{ getKeywordSubsectionNumber('optimizeSubheadings') }}.
            Optimize subheadings
          </h2>
          <result7-paragraph
            :isWhiteGloveUser="false"
            :isMissionControlSession="false"
            :isDemo="false"
            :isLoading="false"
            :pageRun="null"
            :pAIResult="keywordSections.optimizeSubheadings"
            :calculated_titleAIResult="null"
            :editorDisplayByDEfault="true"
            :calculated_pageTitleAIResult="null"
            :editingParagraphIdxInResult.sync="currentEditingIdx"
            :handleParagraphEditDebounced="handleKeywordEdit"
            :handleRemoveContent="handleRemoveKeyword"
            :regenerateForParagraphOneSection="regenerateForParagraphOneSection"
            :seletedParagraphSection.sync="selectedParagraphSection"
            :showParagraphOneSectionHistoryModal.sync="showParagraphOneSectionHistoryModal"
            :textToCssId="textToCssId"
            :setNeedsToSave="setNeedsToSave"
            :getAllContentPromptDataForPSection="getAllContentPromptDataForPSection"
            :aiGeneratedImagesMap="{}"
            :imageAIModalParams.sync="imageAIModalParams"
            :deleteImageAIModalParams.sync="deleteImageAIModalParams"
            :showContnentPromptGenerationModal="showContnentPromptGenerationModal"
            :showEditPopupForRichItem="showEditPopupForRichItem"
            :showHistoryForRichItem="showHistoryForRichItem"
            :highlightKeywords="highlightKeywords"
            @clickCheckOptimizationScore="onCheckOptimizationScore"
            @startOver="onStartOver"
            :displayHead="false"
            :showTags="false"
            :saveParentChanges="saveChanges"
          />
        </div>

        <!-- Optimize your Search Engine Title -->
        <div
          v-if="keywordSections.optimizeSearchTitle.length > 0"
          class="keyword-subsection"
        >
          <h2 class="sub-section-title">
            {{ getKeywordSubsectionNumber('optimizeSearchTitle') }}.
            Optimize your Search Engine Title
          </h2>
          <result7-paragraph
            :isWhiteGloveUser="false"
            :isMissionControlSession="false"
            :isDemo="false"
            :isLoading="false"
            :pageRun="null"
            :pAIResult="keywordSections.optimizeSearchTitle"
            :calculated_titleAIResult="null"
            :editorDisplayByDEfault="true"
            :calculated_pageTitleAIResult="null"
            :editingParagraphIdxInResult.sync="currentEditingIdx"
            :handleParagraphEditDebounced="handleKeywordEdit"
            :handleRemoveContent="handleRemoveKeyword"
            :regenerateForParagraphOneSection="regenerateForParagraphOneSection"
            :seletedParagraphSection.sync="selectedParagraphSection"
            :showParagraphOneSectionHistoryModal.sync="showParagraphOneSectionHistoryModal"
            :textToCssId="textToCssId"
            :setNeedsToSave="setNeedsToSave"
            :getAllContentPromptDataForPSection="getAllContentPromptDataForPSection"
            :aiGeneratedImagesMap="{}"
            :imageAIModalParams.sync="imageAIModalParams"
            :deleteImageAIModalParams.sync="deleteImageAIModalParams"
            :showContnentPromptGenerationModal="showContnentPromptGenerationModal"
            :showEditPopupForRichItem="showEditPopupForRichItem"
            :showHistoryForRichItem="showHistoryForRichItem"
            :highlightKeywords="highlightKeywords"
            @clickCheckOptimizationScore="onCheckOptimizationScore"
            @startOver="onStartOver"
            :displayHead="false"
            :showTags="false"
            :saveParentChanges="saveChanges"
          />
        </div>

        <!-- Optimize Page Title -->
        <div
          v-if="keywordSections.optimizePageTitle.length > 0"
          class="keyword-subsection"
        >
          <h2 class="sub-section-title">
            {{ getKeywordSubsectionNumber('optimizePageTitle') }}.
            Optimize Page Title
          </h2>
          <result7-paragraph
            :isWhiteGloveUser="false"
            :isMissionControlSession="false"
            :isDemo="false"
            :isLoading="false"
            :pageRun="null"
            :pAIResult="keywordSections.optimizePageTitle"
            :calculated_titleAIResult="null"
            :editorDisplayByDEfault="true"
            :calculated_pageTitleAIResult="null"
            :editingParagraphIdxInResult.sync="currentEditingIdx"
            :handleParagraphEditDebounced="handleKeywordEdit"
            :handleRemoveContent="handleRemoveKeyword"
            :regenerateForParagraphOneSection="regenerateForParagraphOneSection"
            :seletedParagraphSection.sync="selectedParagraphSection"
            :showParagraphOneSectionHistoryModal.sync="showParagraphOneSectionHistoryModal"
            :textToCssId="textToCssId"
            :setNeedsToSave="setNeedsToSave"
            :getAllContentPromptDataForPSection="getAllContentPromptDataForPSection"
            :aiGeneratedImagesMap="{}"
            :imageAIModalParams.sync="imageAIModalParams"
            :deleteImageAIModalParams.sync="deleteImageAIModalParams"
            :showContnentPromptGenerationModal="showContnentPromptGenerationModal"
            :showEditPopupForRichItem="showEditPopupForRichItem"
            :showHistoryForRichItem="showHistoryForRichItem"
            :highlightKeywords="highlightKeywords"
            @clickCheckOptimizationScore="onCheckOptimizationScore"
            @startOver="onStartOver"
            :displayHead="false"
            :showTags="false"
            :saveParentChanges="saveChanges"
          />
        </div>

        <!-- Expand your content -->
        <div
          v-if="keywordSections.expandContent.length > 0"
          class="keyword-subsection"
        >
          <h2 class="sub-section-title">
            {{ getKeywordSubsectionNumber('expandContent') }}.
            Expand your content
          </h2>
          <result7-paragraph
            :isWhiteGloveUser="false"
            :isMissionControlSession="false"
            :isDemo="false"
            :isLoading="false"
            :pageRun="null"
            :pAIResult="keywordSections.expandContent"
            :calculated_titleAIResult="null"
            :editorDisplayByDEfault="true"
            :calculated_pageTitleAIResult="null"
            :editingParagraphIdxInResult.sync="currentEditingIdx"
            :handleParagraphEditDebounced="handleKeywordEdit"
            :handleRemoveContent="handleRemoveKeyword"
            :regenerateForParagraphOneSection="regenerateForParagraphOneSection"
            :seletedParagraphSection.sync="selectedParagraphSection"
            :showParagraphOneSectionHistoryModal.sync="showParagraphOneSectionHistoryModal"
            :textToCssId="textToCssId"
            :setNeedsToSave="setNeedsToSave"
            :getAllContentPromptDataForPSection="getAllContentPromptDataForPSection"
            :aiGeneratedImagesMap="{}"
            :imageAIModalParams.sync="imageAIModalParams"
            :deleteImageAIModalParams.sync="deleteImageAIModalParams"
            :showContnentPromptGenerationModal="showContnentPromptGenerationModal"
            :showEditPopupForRichItem="showEditPopupForRichItem"
            :showHistoryForRichItem="showHistoryForRichItem"
            :highlightKeywords="highlightKeywords"
            @clickCheckOptimizationScore="onCheckOptimizationScore"
            @startOver="onStartOver"
            :displayHead="false"
            :showTags="false"
            :saveParentChanges="saveChanges"
          />
        </div>

        <!-- Improve main content -->
        <div
          v-if="keywordSections.improveMainContent.length > 0"
          class="keyword-subsection"
        >
          <h2 class="sub-section-title">
            {{ getKeywordSubsectionNumber('improveMainContent') }}.
            Improve main content
          </h2>
          <result7-paragraph
            :isWhiteGloveUser="false"
            :isMissionControlSession="false"
            :isDemo="false"
            :isLoading="false"
            :pageRun="null"
            :pAIResult="keywordSections.improveMainContent"
            :calculated_titleAIResult="null"
            :editorDisplayByDEfault="true"
            :calculated_pageTitleAIResult="null"
            :editingParagraphIdxInResult.sync="currentEditingIdx"
            :handleParagraphEditDebounced="handleKeywordEdit"
            :handleRemoveContent="handleRemoveKeyword"
            :regenerateForParagraphOneSection="regenerateForParagraphOneSection"
            :seletedParagraphSection.sync="selectedParagraphSection"
            :showParagraphOneSectionHistoryModal.sync="showParagraphOneSectionHistoryModal"
            :textToCssId="textToCssId"
            :setNeedsToSave="setNeedsToSave"
            :getAllContentPromptDataForPSection="getAllContentPromptDataForPSection"
            :aiGeneratedImagesMap="{}"
            :imageAIModalParams.sync="imageAIModalParams"
            :deleteImageAIModalParams.sync="deleteImageAIModalParams"
            :showContnentPromptGenerationModal="showContnentPromptGenerationModal"
            :showEditPopupForRichItem="showEditPopupForRichItem"
            :showHistoryForRichItem="showHistoryForRichItem"
            :highlightKeywords="highlightKeywords"
            @clickCheckOptimizationScore="onCheckOptimizationScore"
            @startOver="onStartOver"
            :displayHead="false"
            :showTags="false"
            :saveParentChanges="saveChanges"
          />
        </div>
      </div>

      <!-- SECTION 2: Schema Markup -->
      <div
        v-if="keywordSections.schemaMarkup.length > 0"
        class="report-section"
      >
        <div class="section-header">SECTION 2</div>
        <h2 class="section-title">Schema Markup</h2>
        <result7-paragraph
          :isWhiteGloveUser="false"
          :isMissionControlSession="false"
          :isDemo="false"
          :isLoading="false"
          :pageRun="null"
          :pAIResult="keywordSections.schemaMarkup"
          :calculated_titleAIResult="null"
          :editorDisplayByDEfault="true"
          :calculated_pageTitleAIResult="null"
          :editingParagraphIdxInResult.sync="currentEditingIdx"
          :handleParagraphEditDebounced="handleKeywordEdit"
          :handleRemoveContent="handleRemoveKeyword"
          :regenerateForParagraphOneSection="regenerateForParagraphOneSection"
          :seletedParagraphSection.sync="selectedParagraphSection"
          :showParagraphOneSectionHistoryModal.sync="showParagraphOneSectionHistoryModal"
          :textToCssId="textToCssId"
          :setNeedsToSave="setNeedsToSave"
          :getAllContentPromptDataForPSection="getAllContentPromptDataForPSection"
          :aiGeneratedImagesMap="{}"
          :imageAIModalParams.sync="imageAIModalParams"
          :deleteImageAIModalParams.sync="deleteImageAIModalParams"
          :showContnentPromptGenerationModal="showContnentPromptGenerationModal"
          :showEditPopupForRichItem="showEditPopupForRichItem"
          :showHistoryForRichItem="showHistoryForRichItem"
          :highlightKeywords="highlightKeywords"
          @clickCheckOptimizationScore="onCheckOptimizationScore"
          @startOver="onStartOver"
          :displayHead="false"
          :showTags="false"
          :saveParentChanges="saveChanges"
        />
      </div>

      <!-- SECTION 3: Improve page structure -->
      <div
        v-if="keywordSections.improvePageStructure.length > 0"
        class="report-section"
      >
        <div class="section-header">SECTION 3</div>
        <h2 class="section-title">Improve Page Structure</h2>
        <result7-paragraph
          :isWhiteGloveUser="false"
          :isMissionControlSession="false"
          :isDemo="false"
          :isLoading="false"
          :pageRun="null"
          :pAIResult="keywordSections.improvePageStructure"
          :calculated_titleAIResult="null"
          :editorDisplayByDEfault="true"
          :calculated_pageTitleAIResult="null"
          :editingParagraphIdxInResult.sync="currentEditingIdx"
          :handleParagraphEditDebounced="handleKeywordEdit"
          :handleRemoveContent="handleRemoveKeyword"
          :regenerateForParagraphOneSection="regenerateForParagraphOneSection"
          :seletedParagraphSection.sync="selectedParagraphSection"
          :showParagraphOneSectionHistoryModal.sync="showParagraphOneSectionHistoryModal"
          :textToCssId="textToCssId"
          :setNeedsToSave="setNeedsToSave"
          :getAllContentPromptDataForPSection="getAllContentPromptDataForPSection"
          :aiGeneratedImagesMap="{}"
          :imageAIModalParams.sync="imageAIModalParams"
          :deleteImageAIModalParams.sync="deleteImageAIModalParams"
          :showContnentPromptGenerationModal="showContnentPromptGenerationModal"
          :showEditPopupForRichItem="showEditPopupForRichItem"
          :showHistoryForRichItem="showHistoryForRichItem"
          :highlightKeywords="highlightKeywords"
          @clickCheckOptimizationScore="onCheckOptimizationScore"
          @startOver="onStartOver"
          :displayHead="false"
          :showTags="false"
          :saveParentChanges="saveChanges"
        />
      </div>

      <!-- SECTION 4: Title tag optimization in editor -->
      <div v-if="titleItems.length > 0" class="report-section">
        <div class="section-header">SECTION 4</div>
        <h2 class="section-title">Title tag optimization</h2>
        <result7-paragraph
          :isWhiteGloveUser="false"
          :isMissionControlSession="false"
          :isDemo="false"
          :isLoading="false"
          :pageRun="null"
          :pAIResult="titleItems"
          :calculated_titleAIResult="null"
          :editorDisplayByDEfault="true"
          :calculated_pageTitleAIResult="null"
          :editingParagraphIdxInResult.sync="currentEditingIdx"
          :handleParagraphEditDebounced="handleTitleEdit"
          :handleRemoveContent="handleRemoveTitle"
          :regenerateForParagraphOneSection="regenerateForParagraphOneSection"
          :seletedParagraphSection.sync="selectedParagraphSection"
          :showParagraphOneSectionHistoryModal.sync="showParagraphOneSectionHistoryModal"
          :textToCssId="textToCssId"
          :setNeedsToSave="setNeedsToSave"
          :getAllContentPromptDataForPSection="getAllContentPromptDataForPSection"
          :aiGeneratedImagesMap="{}"
          :imageAIModalParams.sync="imageAIModalParams"
          :deleteImageAIModalParams.sync="deleteImageAIModalParams"
          :showContnentPromptGenerationModal="showContnentPromptGenerationModal"
          :showEditPopupForRichItem="showEditPopupForRichItem"
          :showHistoryForRichItem="showHistoryForRichItem"
          :highlightKeywords="highlightKeywords"
          @clickCheckOptimizationScore="onCheckOptimizationScore"
          @startOver="onStartOver"
          :displayHead="false"
          :showTags="false"
          :saveParentChanges="saveChanges"
        />
      </div>

      <!-- SECTION 5: Proposed outline in editor -->
      <div v-if="outlineItems.length > 0" class="report-section">
        <div class="section-header">SECTION 5</div>
        <h2 class="section-title">Proposed outline</h2>
        <result7-paragraph
          :isWhiteGloveUser="false"
          :isMissionControlSession="false"
          :isDemo="false"
          :isLoading="false"
          :pageRun="null"
          :pAIResult="outlineItems"
          :calculated_titleAIResult="null"
          :editorDisplayByDEfault="true"
          :calculated_pageTitleAIResult="null"
          :editingParagraphIdxInResult.sync="currentEditingIdx"
          :handleParagraphEditDebounced="handleOutlineEdit"
          :handleRemoveContent="handleRemoveOutline"
          :regenerateForParagraphOneSection="regenerateForParagraphOneSection"
          :seletedParagraphSection.sync="selectedParagraphSection"
          :showParagraphOneSectionHistoryModal.sync="showParagraphOneSectionHistoryModal"
          :textToCssId="textToCssId"
          :setNeedsToSave="setNeedsToSave"
          :getAllContentPromptDataForPSection="getAllContentPromptDataForPSection"
          :aiGeneratedImagesMap="{}"
          :imageAIModalParams.sync="imageAIModalParams"
          :deleteImageAIModalParams.sync="deleteImageAIModalParams"
          :showContnentPromptGenerationModal="showContnentPromptGenerationModal"
          :showEditPopupForRichItem="showEditPopupForRichItem"
          :showHistoryForRichItem="showHistoryForRichItem"
          :highlightKeywords="highlightKeywords"
          @clickCheckOptimizationScore="onCheckOptimizationScore"
          @startOver="onStartOver"
          :displayHead="false"
          :showTags="false"
          :saveParentChanges="saveChanges"
        />
      </div>

      <!-- SECTION 6: FAQs -->
      <div v-if="faqItems.length > 0" class="report-section">
        <div class="section-header">SECTION 6</div>
        <h2 class="section-title">FAQs with rich results potential</h2>
        <result7-paragraph
          :isWhiteGloveUser="false"
          :isMissionControlSession="false"
          :isDemo="false"
          :isLoading="false"
          :pageRun="null"
          :pAIResult="faqItems"
          :calculated_titleAIResult="null"
          :editorDisplayByDEfault="true"
          :calculated_pageTitleAIResult="null"
          :editingParagraphIdxInResult.sync="currentEditingIdx"
          :handleParagraphEditDebounced="handleFaqEdit"
          :handleRemoveContent="handleRemoveFaq"
          :regenerateForParagraphOneSection="regenerateForParagraphOneSection"
          :seletedParagraphSection.sync="selectedParagraphSection"
          :showParagraphOneSectionHistoryModal.sync="showParagraphOneSectionHistoryModal"
          :textToCssId="textToCssId"
          :setNeedsToSave="setNeedsToSave"
          :getAllContentPromptDataForPSection="getAllContentPromptDataForPSection"
          :aiGeneratedImagesMap="{}"
          :imageAIModalParams.sync="imageAIModalParams"
          :deleteImageAIModalParams.sync="deleteImageAIModalParams"
          :showContnentPromptGenerationModal="showContnentPromptGenerationModal"
          :showEditPopupForRichItem="showEditPopupForRichItem"
          :showHistoryForRichItem="showHistoryForRichItem"
          :highlightKeywords="highlightKeywords"
          @clickCheckOptimizationScore="onCheckOptimizationScore"
          @startOver="onStartOver"
          :displayHead="false"
          :showTags="false"
          :saveParentChanges="saveChanges"
        />
      </div>

      <!-- SECTION 7: SEO Word Work (structured keyword lists rendered in the report) -->
      <div class="report-section">
        <div class="section-header">SECTION 7</div>
        <h2 class="section-title">SEO Word Work</h2>

        <!-- Title keyword suggestions (Add) -->
        <div class="keyword-subsection">
          <h2 class="sub-section-title mb-4">Title keyword suggestions (Add)</h2>
          <v-btn class="mb-3" color="green" @click="addTitleSuggestion('title')" outlined small>Add item</v-btn>
          <div
            v-for="(item, index) in keywordSuggestions && keywordSuggestions.add && keywordSuggestions.add.title || []"
            :key="`title-sugg-${index}`"
            class="mb-3 p-3"
            style="border: 1px solid var(--v-grayColor-base); border-radius: 4px;"
          >
            <div class="d-flex align-center gap5">
              <b style="width:100px;">Keyword:</b> <v-text-field outlined dense hide-details v-model="item.keyword" />
            </div>
            <div class="d-flex align-center gap5">
              <b style="width:100px;">Current (count):</b> <v-text-field outlined dense hide-details v-model="item.current" />
            </div>
            <div class="d-flex align-center gap5">
              <b style="width:100px;">Target (count):</b> <v-text-field outlined dense hide-details v-model="item.target" />
            </div>
            <div class="d-flex align-center gap5">
              <b style="width:100px;">Target min (count):</b> <v-text-field outlined dense hide-details v-model="item.targetMin" />
            </div>
            <div class="d-flex align-center gap5">
              <b style="width:100px;">Target max (count):</b> <v-text-field outlined dense hide-details v-model="item.targetMax" />
            </div>
            <v-btn @click="removeTitleSuggestion('title', index)" outlined small color="red">Remove</v-btn>
          </div>
        </div>

        <!-- Page title keyword suggestions (Add) -->
        <div class="keyword-subsection">
          <h2 class="sub-section-title mb-4">Page title keyword suggestions (Add)</h2>
          <v-btn class="mb-3" color="green" @click="addTitleSuggestion('pageTitle')" outlined small>Add item</v-btn>
          <div
            v-for="(item, index) in keywordSuggestions && keywordSuggestions.add && keywordSuggestions.add.pageTitle || []"
            :key="`pagetitle-sugg-${index}`"
            class="mb-3 p-3"
            style="border: 1px solid var(--v-grayColor-base); border-radius: 4px;"
          >
            <div class="d-flex align-center gap5">
              <b style="width:100px;">Keyword:</b> <v-text-field outlined dense hide-details v-model="item.keyword" />
            </div>
            <div class="d-flex align-center gap5">
              <b style="width:100px;">Current (count):</b> <v-text-field outlined dense hide-details v-model="item.current" />
            </div>
            <div class="d-flex align-center gap5">
              <b style="width:100px;">Target (count):</b> <v-text-field outlined dense hide-details v-model="item.target" />
            </div>
            <div class="d-flex align-center gap5">
              <b style="width:100px;">Target min (count):</b> <v-text-field outlined dense hide-details v-model="item.targetMin" />
            </div>
            <div class="d-flex align-center gap5">
              <b style="width:100px;">Target max (count):</b> <v-text-field outlined dense hide-details v-model="item.targetMax" />
            </div>
            <v-btn @click="removeTitleSuggestion('pageTitle', index)" outlined small color="red">Remove</v-btn>
          </div>
        </div>

        <!-- Title reduce/remove -->
        <div class="keyword-subsection">
          <h2 class="sub-section-title mb-4">Title reduce/remove (only if overoptimization checked)</h2>
          <v-btn class="mb-3" color="green" @click="addKeywordCount('titleReduceAll')" outlined small>Add item</v-btn>
          <div
            v-for="(item, index) in titleReduceAll || []"
            :key="`title-reduce-${index}`"
            class="mb-3 p-3"
            style="border: 1px solid var(--v-grayColor-base); border-radius: 4px;"
          >
            <div class="d-flex align-center gap5">
              <b style="width:100px;">Keyword:</b> <v-text-field outlined dense hide-details v-model="item.keyword" />
            </div>
            <div class="d-flex align-center gap5">
              <b style="width:100px;">Current (count):</b> <v-text-field outlined dense hide-details v-model="item.current" />
            </div>
            <div class="d-flex align-center gap5">
              <b style="width:100px;">Target (count):</b> <v-text-field outlined dense hide-details v-model="item.target" />
            </div>
            <div class="d-flex align-center gap5">
              <b style="width:100px;">Target min (count):</b> <v-text-field outlined dense hide-details v-model="item.targetMin" />
            </div>
            <div class="d-flex align-center gap5">
              <b style="width:100px;">Target max (count):</b> <v-text-field outlined dense hide-details v-model="item.targetMax" />
            </div>
            <v-btn @click="removeKeywordCount('titleReduceAll', index)" outlined small color="red">Remove</v-btn>
          </div>
        </div>

        <!-- Subheadings increase -->
        <div class="keyword-subsection">
          <h2 class="sub-section-title mb-4">Subheadings increase suggestions</h2>
          <v-btn class="mb-3" color="green" @click="addKeywordCount('subHeadingsAddAll')" outlined small>Add item</v-btn>
          <div
            v-for="(item, index) in subHeadingsAddAll || []"
            :key="`subh-add-${index}`"
            class="mb-3 p-3"
            style="border: 1px solid var(--v-grayColor-base); border-radius: 4px;"
          >
            <div class="d-flex align-center gap5">
              <b style="width:100px;">Keyword:</b> <v-text-field outlined dense hide-details v-model="item.keyword" />
            </div>
            <div class="d-flex align-center gap5">
              <b style="width:100px;">Current (count):</b> <v-text-field outlined dense hide-details v-model="item.current" />
            </div>
            <div class="d-flex align-center gap5">
              <b style="width:100px;">Target (count):</b> <v-text-field outlined dense hide-details v-model="item.target" />
            </div>
            <div class="d-flex align-center gap5">
              <b style="width:100px;">Target min (count):</b> <v-text-field outlined dense hide-details v-model="item.targetMin" />
            </div>
            <div class="d-flex align-center gap5">
              <b style="width:100px;">Target max (count):</b> <v-text-field outlined dense hide-details v-model="item.targetMax" />
            </div>
            <v-btn @click="removeKeywordCount('subHeadingsAddAll', index)" outlined small color="red">Remove</v-btn>
          </div>
        </div>

        <!-- Subheadings decrease -->
        <div class="keyword-subsection">
          <h2 class="sub-section-title mb-4">Subheadings decrease suggestions (only if overoptimization checked)</h2>
          <v-btn class="mb-3" color="green" @click="addKeywordCount('subHeadingsReduceAll')" outlined small>Add item</v-btn>
          <div
            v-for="(item, index) in subHeadingsReduceAll || []"
            :key="`subh-reduce-${index}`"
            class="mb-3 p-3"
            style="border: 1px solid var(--v-grayColor-base); border-radius: 4px;"
          >
            <div class="d-flex align-center gap5">
              <b style="width:100px;">Keyword:</b> <v-text-field outlined dense hide-details v-model="item.keyword" />
            </div>
            <div class="d-flex align-center gap5">
              <b style="width:100px;">Current (count):</b> <v-text-field outlined dense hide-details v-model="item.current" />
            </div>
            <div class="d-flex align-center gap5">
              <b style="width:100px;">Target (count):</b> <v-text-field outlined dense hide-details v-model="item.target" />
            </div>
            <div class="d-flex align-center gap5">
              <b style="width:100px;">Target min (count):</b> <v-text-field outlined dense hide-details v-model="item.targetMin" />
            </div>
            <div class="d-flex align-center gap5">
              <b style="width:100px;">Target max (count):</b> <v-text-field outlined dense hide-details v-model="item.targetMax" />
            </div>
            <v-btn @click="removeKeywordCount('subHeadingsReduceAll', index)" outlined small color="red">Remove</v-btn>
          </div>
        </div>

        <!-- Paragraphs increase -->
        <div class="keyword-subsection">
          <h2 class="sub-section-title mb-4">Paragraphs increase suggestions</h2>
          <v-btn class="mb-3" color="green" @click="addKeywordCount('pAddAll')" outlined small>Add item</v-btn>
          <div
            v-for="(item, index) in pAddAll || []"
            :key="`p-add-${index}`"
            class="mb-3 p-3"
            style="border: 1px solid var(--v-grayColor-base); border-radius: 4px;"
          >
            <div class="d-flex align-center gap5">
              <b style="width:100px;">Keyword:</b> <v-text-field outlined dense hide-details v-model="item.keyword" />
            </div>
            <div class="d-flex align-center gap5">
              <b style="width:100px;">Current (count):</b> <v-text-field outlined dense hide-details v-model="item.current" />
            </div>
            <div class="d-flex align-center gap5">
              <b style="width:100px;">Target (count):</b> <v-text-field outlined dense hide-details v-model="item.target" />
            </div>
            <div class="d-flex align-center gap5">
              <b style="width:100px;">Target min (count):</b> <v-text-field outlined dense hide-details v-model="item.targetMin" />
            </div>
            <div class="d-flex align-center gap5">
              <b style="width:100px;">Target max (count):</b> <v-text-field outlined dense hide-details v-model="item.targetMax" />
            </div>
            <v-btn @click="removeKeywordCount('pAddAll', index)" outlined small color="red">Remove</v-btn>
          </div>
        </div>

        <!-- Paragraphs decrease -->
        <div class="keyword-subsection">
          <h2 class="sub-section-title mb-4">Paragraphs decrease suggestions (only if overoptimization checked)</h2>
          <v-btn class="mb-3" color="green" @click="addKeywordCount('pReduceAll')" outlined small>Add item</v-btn>
          <div
            v-for="(item, index) in pReduceAll || []"
            :key="`p-reduce-${index}`"
            class="mb-3 p-3"
            style="border: 1px solid var(--v-grayColor-base); border-radius: 4px;"
          >
            <div class="d-flex align-center gap5">
              <b style="width:100px;">Keyword:</b> <v-text-field outlined dense hide-details v-model="item.keyword" />
            </div>
            <div class="d-flex align-center gap5">
              <b style="width:100px;">Current (count):</b> <v-text-field outlined dense hide-details v-model="item.current" />
            </div>
            <div class="d-flex align-center gap5">
              <b style="width:100px;">Target (count):</b> <v-text-field outlined dense hide-details v-model="item.target" />
            </div>
            <div class="d-flex align-center gap5">
              <b style="width:100px;">Target min (count):</b> <v-text-field outlined dense hide-details v-model="item.targetMin" />
            </div>
            <div class="d-flex align-center gap5">
              <b style="width:100px;">Target max (count):</b> <v-text-field outlined dense hide-details v-model="item.targetMax" />
            </div>
            <v-btn @click="removeKeywordCount('pReduceAll', index)" outlined small color="red">Remove</v-btn>
          </div>
        </div>

        <!-- Related keywords (old search results) -->
        <div class="keyword-subsection">
          <h2 class="sub-section-title mb-4">Related keywords (from old search results)</h2>
          <v-btn class="mb-3" color="green" @click="addRelatedSearch('old')" outlined small>Add item</v-btn>
          <div
            v-for="(item, index) in old_related_searches || []"
            :key="`old-related-${index}`"
            class="mb-3 p-3"
            style="border: 1px solid var(--v-grayColor-base); border-radius: 4px;"
          >
            <div class="d-flex align-center gap5">
              <b style="width:100px;">Query:</b> <v-text-field outlined dense hide-details v-model="item.query" />
            </div>
            <div class="d-flex align-center gap5">
              <b style="width:100px;">Link:</b> <v-text-field outlined dense hide-details v-model="item.link" />
            </div>
            <div class="d-flex align-center gap5">
              <b style="width:100px;">SERP API Link:</b> <v-text-field outlined dense hide-details v-model="item.serpapi_link" />
            </div>
            <v-btn @click="removeRelatedSearch('old', index)" outlined small color="red">Remove</v-btn>
          </div>
        </div>

        <!-- Related keywords (new search results) -->
        <div class="keyword-subsection">
          <h2 class="sub-section-title mb-4">Related keywords (from new search results)</h2>
          <v-btn class="mb-3" color="green" @click="addRelatedSearch('new')" outlined small>Add item</v-btn>
          <div
            v-for="(item, index) in new_related_searches || []"
            :key="`new-related-${index}`"
            class="mb-3 p-3"
            style="border: 1px solid var(--v-grayColor-base); border-radius: 4px;"
          >
            <div class="d-flex align-center gap5">
              <b style="width:100px;">Query:</b> <v-text-field outlined dense hide-details v-model="item.query" />
            </div>
            <div class="d-flex align-center gap5">
              <b style="width:100px;">Link:</b> <v-text-field outlined dense hide-details v-model="item.link" />
            </div>
            <div class="d-flex align-center gap5">
              <b style="width:100px;">SERP API Link:</b> <v-text-field outlined dense hide-details v-model="item.serpapi_link" />
            </div>
            <v-btn @click="removeRelatedSearch('new', index)" outlined small color="red">Remove</v-btn>
          </div>
        </div>
      </div>

      <!-- SECTION 8: EEAT Signals -->
      <div class="report-section">
        <div class="section-header">SECTION 8</div>
        <h2 class="section-title">EEAT Signals</h2>

        <div class="keyword-subsection">
          <v-btn class="mb-3" color="green" @click="addEatSchemaType" outlined small>Add item</v-btn>
          <div
            v-for="(item, index) in eatSchemaTypes || []"
            :key="`eeat-${index}`"
            class="mb-3 p-3"
            style="border: 1px solid var(--v-grayColor-base); border-radius: 4px;"
          >
            <div class="d-flex align-center gap5">
              <b style="width:100px;">Signal type:</b> <v-text-field outlined dense hide-details v-model="item.signal" />
            </div>
            <div class="d-flex align-center gap5">
              <b style="width:100px;">Count:</b> <v-text-field outlined dense hide-details v-model="item.count" />
            </div>
            <v-btn @click="removeEatSchemaType(index)" outlined small color="red">Remove</v-btn>
          </div>
        </div>
      </div>

      <!-- Save Button -->
      <div class="mt-4 d-flex is-justify-content-flex-end">
        <button
          @click="saveChanges"
          :disabled="isSaving || isLoading"
          class="save-btn text-white font-bold py-2 px-4 rounded"
        >
          {{ isSaving ? "Saving..." : "Save Changes" }}
        </button>
        <span v-if="saveStatus" class="ml-3">{{ saveStatus }}</span>
      </div>
    </div>
  </div>
</template>

<script>
import Result7Paragraph from "@/components/FullPageSourceEditorV2/components/AiWriter/results/Result7Paragraph.vue";

export default {
  components: { Result7Paragraph },

  data() {
    return {
      reportData: null,

      faqItems: [],
      titleItems: [],
      outlineItems: [],

      keywordSections: {
        reviewMainKeywords: [],
        incorporateKeywords: [],
        optimizeSubheadings: [],
        optimizeSearchTitle: [],
        optimizePageTitle: [],
        improveMainContent: [],
        expandContent: [],
        schemaMarkup: [],
        improvePageStructure: []
      },

      // 🔢 Order of subsections for SECTION 1 numbering
      keywordSubsectionOrder: [
        "reviewMainKeywords",
        "incorporateKeywords",
        "optimizeSubheadings",
        "optimizeSearchTitle",
        "optimizePageTitle",
        "expandContent",
        "improveMainContent"
      ],

      // Structured fields that the generated report renders directly (the
      // "flattened" report fields). These are edited by the structured sections
      // below and written back verbatim on save so they show up in the report.
      eatSchemaTypes: [],
      old_related_searches: [],
      new_related_searches: [],
      subHeadingsAddAll: [],
      subHeadingsReduceAll: [],
      pAddAll: [],
      pReduceAll: [],
      titleReduceAll: [],
      keywordSuggestions: {},

      titleResult: null,
      pageTitleResult: null,

      currentEditingIdx: 0,
      selectedParagraphSection: 0,
      showParagraphOneSectionHistoryModal: false,

      imageAIModalParams: null,
      deleteImageAIModalParams: null,

      isLoading: false,
      error: null,

      isSaving: false,
      saveStatus: "",
      saveTimeout: null,

      siloKeywordId: null
    };
  },

  created() {
    this.siloKeywordId = this.$route.params.siloKeywordId;
  },

  computed: {
    hasKeywordUpdates() {
      const keys = [
        "reviewMainKeywords",
        "incorporateKeywords",
        "optimizeSubheadings",
        "optimizeSearchTitle",
        "optimizePageTitle",
        "improveMainContent",
        "expandContent"
      ];
      return keys.some(
        (key) =>
          Array.isArray(this.keywordSections[key]) &&
          this.keywordSections[key].length > 0
      );
    },

    storeReportData() {
      return this.$store.state.reportsData;
    },
    storeLoading() {
      return this.$store.state.reportsLoading;
    },
    storeError() {
      return this.$store.state.reportsError;
    }
  },

  watch: {
    storeLoading(newVal) {
      this.isLoading = newVal;
    },
    storeError(newVal) {
      if (newVal) {
        this.error = newVal;
      }
    }
  },

  methods: {
    _stripHtmlTags(html) {
      if (!html) return "";
      const tmp = document.createElement("div");
      tmp.innerHTML = html;
      return tmp.textContent || tmp.innerText || "";
    },

    async loadReportsData() {
      this.isLoading = true;
      this.error = null;

      try {
        await this.$store.dispatch("loadReportsData", {
          siloKeywordId: this.siloKeywordId
        });

        const report = this.$store.state.reportsData;
        this.reportData = report;

        if (!report || typeof report !== "object" || Array.isArray(report)) {
          this.error = "Report is not ready.";
          return;
        }

        if (report.status === "FAILURE") {
          this.error = report.msg || "Report is not ready.";
          return;
        }

        const headerImage =
          report.headerImage || this.$store.state.reportsHeaderImage;

        // Case 1: backend sent error object instead of base64
        // headerImage = { msg: "Invalid Report Type.", status: "FAILURE" }
        if (
          headerImage &&
          typeof headerImage === "object" &&
          headerImage.status === "FAILURE"
        ) {
          this.error = headerImage.msg || "Invalid Report Type.";
          return;
        }

        this.buildCustomSections();
      } catch (err) {
        console.log(err, "ASADSAFD");
        this.error =
          this.$store.state.reportsError ||
          "Failed to load data. Please try again.";
      } finally {
        this.isLoading = false;
      }
    },

    buildCustomSections() {
      const data = this.reportData;
      if (!data) return;

      // Structured report fields (mirror the new review-optimization editor and
      // the flattened fields the report template renders).
      this.eatSchemaTypes = Array.isArray(data.eatSchemaTypes) ? data.eatSchemaTypes : [];
      this.old_related_searches = Array.isArray(data.old_related_searches) ? data.old_related_searches : [];
      this.new_related_searches = Array.isArray(data.new_related_searches) ? data.new_related_searches : [];
      this.subHeadingsAddAll = Array.isArray(data.subHeadingsAddAll) ? data.subHeadingsAddAll : [];
      this.subHeadingsReduceAll = Array.isArray(data.subHeadingsReduceAll) ? data.subHeadingsReduceAll : [];
      this.pAddAll = Array.isArray(data.pAddAll) ? data.pAddAll : [];
      this.pReduceAll = Array.isArray(data.pReduceAll) ? data.pReduceAll : [];
      this.titleReduceAll = Array.isArray(data.titleReduceAll) ? data.titleReduceAll : [];
      this.keywordSuggestions = data.keywordSuggestions && typeof data.keywordSuggestions === "object"
        ? data.keywordSuggestions
        : {};

      // Reset
      this.faqItems = [];
      this.titleItems = [];
      this.outlineItems = [];
      this.keywordSections = {
        reviewMainKeywords: [],
        incorporateKeywords: [],
        optimizeSubheadings: [],
        optimizeSearchTitle: [],
        optimizePageTitle: [],
        improveMainContent: [],
        expandContent: [],
        schemaMarkup: [],
        improvePageStructure: []
      };

      // FAQ items
      if (
        data.faq_data &&
        Array.isArray(data.faq_data.faqs) &&
        data.faq_data.faqs.length > 0
      ) {
        data.faq_data.faqs.forEach((faq, faqIndex) => {
          this.faqItems.push({
            answer: faq.answer || `<p>${faq.answer || ""}</p>`,
            subheadingInfo: {
              tagName: "H3",
              tagText: faq.question
            },
            _meta: {
              kind: "faq",
              faqIndex,
              sectionType: "faq"
            }
          });
        });
      }

      // Keyword updates
      const keywordUpdates = Array.isArray(data.keywordUpdates)
        ? [...data.keywordUpdates]
        : [];
      keywordUpdates.sort((a, b) => (a.index || 0) - (b.index || 0));

      keywordUpdates.forEach((update, updateIndex) => {
        const sectionLabel = update.section_label || "";
        let rawItems = Array.isArray(update.data) ? [...update.data] : [];
        let faqListHtml = null;

        if (sectionLabel === "Expand your content") {
          const filteredItems = [];
          rawItems.forEach((item) => {
            if (faqListHtml || typeof item !== "string") {
              filteredItems.push(item);
              return;
            }

            const { cleanedHtml, faqHtml } = this._extractFaqBlockFromHtml(
              item
            );

            if (faqHtml) {
              faqListHtml = faqHtml;
              if (cleanedHtml && cleanedHtml.trim()) {
                filteredItems.push(cleanedHtml.trim());
              }
            } else {
              filteredItems.push(item);
            }
          });

          rawItems = filteredItems;
        }

        // Improve page structure: one row per item (keep span structure)
        if (sectionLabel === "Improve page structure") {
          rawItems.forEach((item, rowIndex) => {
            if (typeof item !== "string") return;
            const html = item.trim();
            if (!html) return;

            let headingText = "";
            let bodyHtml = "";

            const container = document.createElement("div");
            container.innerHTML = html;

            const spanEl = container.querySelector("span");

            if (spanEl) {
              headingText = (spanEl.textContent || "").trim();

              const spanParent = spanEl.parentNode;
              if (spanParent) {
                spanParent.removeChild(spanEl);
              }

              bodyHtml = container.innerHTML.trim();
            } else {
              bodyHtml = html;
            }

            if (bodyHtml) {
              bodyHtml = bodyHtml.replace(/^[\s:–\-]+/, "").trim();
            }

            if (bodyHtml && !bodyHtml.startsWith("<")) {
              bodyHtml = `<p>${bodyHtml}</p>`;
            }

            this.keywordSections.improvePageStructure.push({
              answer: bodyHtml || "",
              subheadingInfo: {
                tagName: "H3",
                tagText: headingText
              },
              _meta: {
                kind: "keyword_row",
                updateIndex,
                rowIndex,
                sectionLabel,
                sectionType: "keyword"
              }
            });
          });

          return;
        }

        // Optimize schema markup: combine schema_data + recommendations
        if (sectionLabel === "Optimize schema markup") {
          const schemaData = Array.isArray(data.schema_data)
            ? data.schema_data
            : [];

          let existingSchemaHtml = "";
          if (schemaData.length > 0) {
            existingSchemaHtml = [
              "<p>Existing schema types detected on the page:</p>",
              "<ul>",
              ...schemaData.map(
                (s) =>
                  `<li>${s.type || "Unknown"} (count: ${
                    s.count != null ? s.count : "N/A"
                  })</li>`
              ),
              "</ul>"
            ].join("\n");
          }

          const consolidatedContent = `${existingSchemaHtml}`.trim();
          const strippedContent =
            this._stripHtmlTags(consolidatedContent).trim();

          if (!strippedContent) {
            return;
          }

          const itemObj = {
            answer: consolidatedContent,
            subheadingInfo: { tagName: "", tagText: "" },
            _meta: {
              kind: "schema_consolidated",
              updateIndex,
              sectionLabel,
              sectionType: "schema",
              itemCount: rawItems.length
            }
          };

          this.keywordSections.schemaMarkup.push(itemObj);
          return;
        }

        // Default consolidation
        let consolidatedContent = rawItems
          .map((item) => {
            if (typeof item !== "string") return "";
            const trimmed = item.trim();
            if (!trimmed) return "";
            if (trimmed.startsWith("<")) {
              return trimmed;
            }
            return `<p>${trimmed}</p>`;
          })
          .join("\n");

        const strippedContent =
          this._stripHtmlTags(consolidatedContent).trim();

        const hasMainContent =
          !!strippedContent && consolidatedContent !== "<p></p>";

        if (!hasMainContent && sectionLabel !== "Expand your content") {
          return;
        }

        const itemObj = hasMainContent
          ? {
              answer: consolidatedContent,
              subheadingInfo: { tagName: "", tagText: "" },
              _meta: {
                kind: "keyword_consolidated",
                updateIndex,
                sectionLabel,
                sectionType: "keyword",
                itemCount: rawItems.length
              }
            }
          : null;

        if (!itemObj && sectionLabel !== "Expand your content") {
          return;
        }

        switch (sectionLabel) {
          case "Review and update main keywords":
            this.keywordSections.reviewMainKeywords.push(itemObj);
            break;
          case "Incorporate new related keywords":
            this.keywordSections.incorporateKeywords.push(itemObj);
            break;
          case "Optimize subheadings":
            this.keywordSections.optimizeSubheadings.push(itemObj);
            break;
          case "Optimize your Search Engine Title":
            this.keywordSections.optimizeSearchTitle.push(itemObj);
            break;
          case "Optimize Page Title":
            this.keywordSections.optimizePageTitle.push(itemObj);
            break;
          case "Improve main content":
            this.keywordSections.improveMainContent.push(itemObj);
            break;
          case "Expand your content":
            if (itemObj) {
              this.keywordSections.expandContent.push({
                ...itemObj,
                _meta: {
                  ...itemObj._meta,
                  kind: "expand_main_content"
                }
              });
            }

            const relatedQuestionsHtml =
              faqListHtml ||
              this._buildFaqQuestionsHtml(
                Array.isArray(update.faqData) ? update.faqData : []
              );

            if (relatedQuestionsHtml) {
              this.keywordSections.expandContent.push({
                answer: relatedQuestionsHtml,
                subheadingInfo: { tagName: "", tagText: "" },
                _meta: {
                  kind: "expand_related_questions_block",
                  updateIndex,
                  sectionLabel,
                  sectionType: "keyword",
                  originalFaqData: JSON.parse(
                    JSON.stringify(
                      Array.isArray(update.faqData) ? update.faqData : []
                    )
                  )
                }
              });
            }
            break;
          default:
            break;
        }
      });

      // Title tag optimization -> show each option as its own heading block
      const rt = data.recommend_title;
      if (rt && (rt.recommendation || (rt.ranked_alternatives || []).length)) {
        const optionDict = {};
        const recommendedOption = rt.recommendation
          ? rt.recommendation.option
          : null;

        if (rt.recommendation && rt.recommendation.option) {
          optionDict[rt.recommendation.option] = rt.recommendation;
        }

        (rt.ranked_alternatives || []).forEach((alt) => {
          if (alt && alt.option) {
            optionDict[alt.option] = alt;
          }
        });

        const renderOption = (letter) => {
          const opt = optionDict[letter];
          if (!opt) return;

          const titleText = opt.draft_title || "";
          const whyText = opt.why || opt.why_strong || "";
          const scoreGain =
            typeof opt.score_gain !== "undefined"
              ? opt.score_gain
              : opt.score_total || "";

          const htmlParts = [];
          if (titleText) {
            htmlParts.push(`<p><strong>Draft title:</strong> ${titleText}</p>`);
          }
          if (whyText) {
            htmlParts.push(`<p><strong>Why it's strong:</strong> ${whyText}</p>`);
          }
          if (scoreGain) {
            htmlParts.push(`<p><strong>Score gain:</strong> ${scoreGain}</p>`);
          }

          const html = htmlParts.join("\n");
          const stripped = this._stripHtmlTags(html).trim();
          if (!stripped) {
            return;
          }

          const isRecommended = recommendedOption === letter;

          this.titleItems.push({
            answer: html,
            subheadingInfo: {
              tagName: "H3",
              tagText: isRecommended
                ? `Option ${letter}`
                : `Option ${letter}`
            },
            _meta: {
              kind: "title_option",
              option: letter,
              recommended: isRecommended
            }
          });
        };

        const optionOrder = ["A", "B", "C", "D", "E"];
        const rendered = new Set();
        optionOrder.forEach((letter) => {
          if (optionDict[letter]) {
            renderOption(letter);
            rendered.add(letter);
          }
        });

        Object.keys(optionDict)
          .filter((letter) => !rendered.has(letter))
          .sort()
          .forEach((letter) => renderOption(letter));
      }

      // Proposed outline -> single editor item
      const od = data.outline_data;
      let outlineObj = null;
      if (od) {
        if (od.outline && od.outline.hierarchy) {
          outlineObj = od.outline;
        } else if (
          od.result &&
          od.result.outline &&
          od.result.outline.hierarchy
        ) {
          outlineObj = od.result.outline;
        }
      }

      if (outlineObj && Array.isArray(outlineObj.hierarchy)) {
        const parts = [];
        parts.push(
          "<p><strong>Proposed outline (H2/H3 structure)</strong></p>"
        );
        parts.push("<ul>");

        outlineObj.hierarchy.forEach((item) => {
          const h2 = item.h2 || {};
          const h3List = item.h3 || [];
          const h2Text = h2.text || "";
          let h2Terms = [];
          if (Array.isArray(h2.builtin)) h2Terms = h2Terms.concat(h2.builtin);
          if (Array.isArray(h2.lsi)) h2Terms = h2Terms.concat(h2.lsi);
          const h2TermsStr = h2Terms.length
            ? ` <em>(KW/LSI: ${h2Terms.join(", ")})</em>`
            : "";

          parts.push(
            `<li><strong>H2:</strong> ${h2Text || ""}${h2TermsStr || ""}`
          );

          if (h3List.length) {
            parts.push("<ul>");
            h3List.forEach((h3) => {
              const h3Text = h3.text || "";
              let h3Terms = [];
              if (Array.isArray(h3.builtin))
                h3Terms = h3Terms.concat(h3.builtin);
              if (Array.isArray(h3.lsi)) h3Terms = h3Terms.concat(h3.lsi);
              const h3TermsStr = h3Terms.length
                ? ` <em>(KW/LSI: ${h3Terms.join(", ")})</em>`
                : "";
              parts.push(
                `<li><strong>H3:</strong> ${h3Text || ""}${
                  h3TermsStr || ""
                }</li>`
              );
            });
            parts.push("</ul>");
          }

          parts.push("</li>");
        });

        parts.push("</ul>");

        const html = parts.join("\n");
        const stripped = this._stripHtmlTags(html).trim();
        if (stripped) {
          this.outlineItems.push({
            answer: html,
            subheadingInfo: {
              tagName: "",
              tagText: "Proposed outline"
            },
            _meta: {
              kind: "outline_suggestion"
            }
          });
        }
      }

      console.log("✅ Built custom sections:", {
        faqItems: this.faqItems.length,
        keywordSections: Object.keys(this.keywordSections).reduce(
          (acc, key) => {
            acc[key] = this.keywordSections[key].length;
            return acc;
          },
          {}
        ),
        titleItems: this.titleItems.length,
        outlineItems: this.outlineItems.length
      });
    },

    getKeywordSubsectionNumber(sectionKey) {
      let count = 0;

      for (const key of this.keywordSubsectionOrder) {
        const items = this.keywordSections[key];
        const hasItems = Array.isArray(items) && items.length > 0;

        if (hasItems) {
          count += 1;
        }

        if (key === sectionKey) {
          // Thanks to the v-if on each section, this will always be true
          return hasItems ? count : null;
        }
      }

      return null;
    },

    handleFaqEdit(idx, html) {
      if (this.faqItems[idx]) {
        this.$set(this.faqItems[idx], "answer", html);
        this.debouncedSave();
      }
    },

    handleRemoveFaq(idx) {
      this.faqItems.splice(idx, 1);
      this.debouncedSave();
    },

    handleKeywordEdit(idx, html) {
      let found = false;
      let index = idx;

      for (const sectionKey in this.keywordSections) {
        const section = this.keywordSections[sectionKey];
        if (!Array.isArray(section)) continue;

        if (index < section.length && !found) {
          this.$set(section[index], "answer", html);
          found = true;
          break;
        }
        index -= section.length;
      }

      if (found) {
        this.debouncedSave();
      }
    },

    handleRemoveKeyword(idx) {
      let found = false;
      let index = idx;

      for (const sectionKey in this.keywordSections) {
        const section = this.keywordSections[sectionKey];
        if (!Array.isArray(section)) continue;

        if (index < section.length && !found) {
          section.splice(index, 1);
          found = true;
          break;
        }
        index -= section.length;
      }

      if (found) {
        this.debouncedSave();
      }
    },

    handleTitleEdit(idx, html) {
      if (this.titleItems[idx]) {
        this.$set(this.titleItems[idx], "answer", html);
        this.debouncedSave();
      }
    },

    handleRemoveTitle(idx) {
      if (idx >= 0 && idx < this.titleItems.length) {
        this.titleItems.splice(idx, 1);
        this.debouncedSave();
      }
    },

    handleOutlineEdit(idx, html) {
      if (this.outlineItems[idx]) {
        this.$set(this.outlineItems[idx], "answer", html);
        this.debouncedSave();
      }
    },

    handleRemoveOutline(idx) {
      if (idx >= 0 && idx < this.outlineItems.length) {
        this.outlineItems.splice(idx, 1);
        this.debouncedSave();
      }
    },

    // --- Structured keyword-count lists (subheadings / paragraphs / title reduce) ---
    addKeywordCount(field) {
      const current = Array.isArray(this[field]) ? this[field] : [];
      this[field] = [
        { current: 0, keyword: "", target: 0, targetMax: 0, targetMin: 0 },
        ...current
      ];
      this.debouncedSave();
    },

    removeKeywordCount(field, index) {
      const current = Array.isArray(this[field]) ? this[field] : [];
      this[field] = current.filter((_, i) => i !== index);
      this.debouncedSave();
    },

    // --- Related keywords (old / new search results) ---
    addRelatedSearch(which) {
      const field =
        which === "new" ? "new_related_searches" : "old_related_searches";
      const current = Array.isArray(this[field]) ? this[field] : [];
      this[field] = [
        { block_position: 1, link: "", query: "", serpapi_link: "" },
        ...current
      ];
      this.debouncedSave();
    },

    removeRelatedSearch(which, index) {
      const field =
        which === "new" ? "new_related_searches" : "old_related_searches";
      const current = Array.isArray(this[field]) ? this[field] : [];
      this[field] = current.filter((_, i) => i !== index);
      this.debouncedSave();
    },

    // --- Title / Page title keyword suggestions (keywordSuggestions.add.*) ---
    addTitleSuggestion(kind) {
      const ks = this.keywordSuggestions && typeof this.keywordSuggestions === "object"
        ? this.keywordSuggestions
        : {};
      const add = ks.add && typeof ks.add === "object" ? ks.add : {};
      const list = Array.isArray(add[kind]) ? add[kind] : [];
      this.keywordSuggestions = {
        ...ks,
        add: {
          ...add,
          [kind]: [
            { current: 0, keyword: "", target: 0, targetMax: 0, targetMin: 0 },
            ...list
          ]
        }
      };
      this.debouncedSave();
    },

    removeTitleSuggestion(kind, index) {
      const ks = this.keywordSuggestions && typeof this.keywordSuggestions === "object"
        ? this.keywordSuggestions
        : {};
      const add = ks.add && typeof ks.add === "object" ? ks.add : {};
      const list = Array.isArray(add[kind]) ? add[kind] : [];
      this.keywordSuggestions = {
        ...ks,
        add: { ...add, [kind]: list.filter((_, i) => i !== index) }
      };
      this.debouncedSave();
    },

    // --- EEAT signals ---
    addEatSchemaType() {
      const current = Array.isArray(this.eatSchemaTypes) ? this.eatSchemaTypes : [];
      this.eatSchemaTypes = [{ count: 1, signal: "" }, ...current];
      this.debouncedSave();
    },

    removeEatSchemaType(index) {
      const current = Array.isArray(this.eatSchemaTypes) ? this.eatSchemaTypes : [];
      this.eatSchemaTypes = current.filter((_, i) => i !== index);
      this.debouncedSave();
    },

    regenerateForParagraphOneSection(idx) {},

    getAllContentPromptDataForPSection(idx) {
      return [];
    },

    showContnentPromptGenerationModal(
      isFirstGenerate,
      key,
      shortenKey,
      uniqueIndex
    ) {
      console.log("showContnentPromptGenerationModal", {
        isFirstGenerate,
        key,
        shortenKey,
        uniqueIndex
      });
    },

    showEditPopupForRichItem(item) {},

    showHistoryForRichItem(key, shortenKey, uniqueIndex) {},

    highlightKeywords(textToHighlight, isHighlightEnabled, isForDocx = false) {
      return textToHighlight;
    },

    textToCssId(text) {
      return (text || "").replace(/[^a-zA-Z0-9]/g, "_");
    },

    onCheckOptimizationScore(eventData) {},

    onStartOver() {},

    setNeedsToSave() {
      this.debouncedSave();
    },

    debouncedSave() {
      if (this.saveTimeout) {
        clearTimeout(this.saveTimeout);
      }
      this.saveTimeout = setTimeout(() => {
        this.saveChanges();
      }, 2000);
    },

    _stripOuterBlock(html) {
      if (!html) return "";
      const container = document.createElement("div");
      container.innerHTML = html.trim();
      if (container.childElementCount === 1) {
        return container.firstElementChild.innerHTML.trim();
      }
      return html.trim();
    },

    _sanitizeFaqQuestion(text) {
      if (!text) return "";
      return text.replace(/^>\s*/, "").trim();
    },

    _extractFaqBlockFromHtml(html) {
      if (typeof html !== "string" || !html.trim()) {
        return { cleanedHtml: html, faqHtml: null };
      }

      const container = document.createElement("div");
      container.innerHTML = html;

      const classBasedUl = container.querySelector("ul.expand-faq-list");
      if (classBasedUl) {
        const faqHtml = classBasedUl.outerHTML;
        classBasedUl.parentNode.removeChild(classBasedUl);
        return {
          cleanedHtml: container.innerHTML.trim(),
          faqHtml
        };
      }

      const nestedUl = container.querySelector("ul ul");
      if (!nestedUl) {
        return { cleanedHtml: html, faqHtml: null };
      }

      const parentLi = nestedUl.parentElement;
      if (!parentLi || parentLi.tagName !== "LI") {
        return { cleanedHtml: html, faqHtml: null };
      }

      const hasExtraContent = Array.from(parentLi.childNodes).some((node) => {
        if (node === nestedUl) {
          return false;
        }

        if (node.nodeType === Node.TEXT_NODE) {
          return node.textContent && node.textContent.trim().length > 0;
        }

        if (node.nodeType === Node.ELEMENT_NODE) {
          return node.tagName !== "UL";
        }

        return false;
      });

      if (hasExtraContent) {
        return { cleanedHtml: html, faqHtml: null };
      }

      const wrapperUl = parentLi.parentElement;
      if (!wrapperUl || wrapperUl.tagName !== "UL") {
        return { cleanedHtml: html, faqHtml: null };
      }

      const listItems = Array.from(wrapperUl.children).filter(
        (child) => child.tagName === "LI"
      );
      if (listItems.length !== 1) {
        return { cleanedHtml: html, faqHtml: null };
      }

      const faqHtml = wrapperUl.outerHTML;
      wrapperUl.parentNode.removeChild(wrapperUl);

      return {
        cleanedHtml: container.innerHTML.trim(),
        faqHtml
      };
    },

    _buildFaqQuestionsHtml(faqList) {
      if (!Array.isArray(faqList) || faqList.length === 0) {
        return "";
      }

      const sanitized = faqList
        .map((faq) =>
          this._sanitizeFaqQuestion((faq && faq.question) || "")
        )
        .filter((text) => text.length > 0);

      if (!sanitized.length) {
        return "";
      }

      return [
        '<ul class="expand-faq-list">',
        ...sanitized.map((text) => `<li>${text}</li>`),
        "</ul>"
      ].join("\n");

    },

    _extractQuestionsFromHtml(html) {
      if (!html) {
        return [];
      }

      const container = document.createElement("div");
      container.innerHTML = html;

      const customListItems = container.querySelectorAll(
        "ul.expand-faq-list li"
      );
      const legacyItems = container.querySelectorAll("ul ul li");
      const targets = customListItems.length
        ? customListItems
        : legacyItems.length
        ? legacyItems
        : container.querySelectorAll("li");

      return Array.from(targets)
        .map((li) => this._sanitizeFaqQuestion(li.textContent || ""))
        .filter((text) => text.length > 0);
    },

    _buildExpandSectionPayload(items, originalUpdate) {
      const contentItems = items.filter(
        (item) =>
          !item._meta || item._meta.kind !== "expand_related_questions_block"
      );

      const combinedHtml = contentItems
        .map((item) => (item.answer || "").trim())
        .filter((html) => this._stripHtmlTags(html).trim().length > 0)
        .join("\n");

      const relatedBlock = items.find(
        (item) => item._meta && item._meta.kind === "expand_related_questions_block"
      );

      let faqData = [];
      if (relatedBlock) {
        const extractedQuestions = this._extractQuestionsFromHtml(
          relatedBlock.answer || ""
        );

        const originalFaqData = Array.isArray(
          relatedBlock._meta && relatedBlock._meta.originalFaqData
        )
          ? relatedBlock._meta.originalFaqData
          : Array.isArray(originalUpdate.faqData)
          ? originalUpdate.faqData
          : [];

        faqData = extractedQuestions.map((questionText, idx) => {
          const baseFaq =
            (Array.isArray(originalFaqData) && originalFaqData[idx]) || {};
          return {
            ...baseFaq,
            question: this._sanitizeFaqQuestion(questionText)
          };
        });
      }

      const sectionData = [];
      if (combinedHtml) {
        sectionData.push(combinedHtml);
      }

      if (!sectionData.length) {
        return null;
      }

      return {
        sectionData,
        faqData
      };
    },

    _parseTitleOptionFields(html) {
      const container = document.createElement("div");
      container.innerHTML = html || "";

      let draftTitle = "";
      let whyText = "";
      let scoreGain = "";

      const paragraphs = Array.from(container.querySelectorAll("p"));

      paragraphs.forEach((p) => {
        const text = (p.textContent || "").trim();
        if (!text) return;

        const lower = text.toLowerCase();

        if (!draftTitle && lower.startsWith("draft title")) {
          draftTitle = text.replace(/^[^:]+:\s*/i, "").trim();
        } else if (!whyText && lower.startsWith("why")) {
          whyText = text.replace(/^[^:]+:\s*/i, "").trim();
        } else if (!scoreGain && lower.startsWith("score gain")) {
          scoreGain = text.replace(/^[^:]+:\s*/i, "").trim();
        }
      });

      if (!draftTitle) {
        draftTitle = this._stripHtmlTags(html).trim();
      }

      return {
        draftTitle,
        whyText,
        scoreGain
      };
    },

    _parseHtmlIntoParts(html) {
      const parts = [];
      const container = document.createElement("div");
      container.innerHTML = (html || "").trim();

      Array.from(container.childNodes).forEach((node) => {
        if (node.nodeType === Node.TEXT_NODE) {
          const text = (node.textContent || "").trim();
          if (text) {
            parts.push(text);
          }
        } else if (node.nodeType === Node.ELEMENT_NODE) {
      if (node.tagName === "P") {
        let inner = node.innerHTML || "";
        inner = inner.replace(/\u00a0/g, " ").trim();
        if (inner) {
          parts.push(`<p>${inner}</p>`);
        }
          } else if (node.tagName === "UL" || node.tagName === "OL") {
            parts.push(node.outerHTML);
          } else {
            const strippedContent = this._stripHtmlTags(
              node.outerHTML
            ).trim();
            if (strippedContent) {
              parts.push(node.outerHTML);
            }
          }
        }
      });

      return parts;
    },

    rebuildUpdatedDataFromSections() {
      const base = this.reportData || {};
      const updatedData = JSON.parse(JSON.stringify(base));

      // --- FAQ data ---
      updatedData.faq_data = { faqs: [] };
      this.faqItems.forEach((item) => {
        const html = item.answer || "";
        const strippedContent = this._stripHtmlTags(html).trim();
        if (strippedContent.length > 0) {
          updatedData.faq_data.faqs.push({
            question: (item.subheadingInfo && item.subheadingInfo.tagText) || "",
            answer: html
          });
        }
      });

      // --- Keyword updates ---
      const originalKeywordUpdates = Array.isArray(base.keywordUpdates)
        ? base.keywordUpdates
        : [];

      const sectionKeyByLabel = {
        "Review and update main keywords": "reviewMainKeywords",
        "Incorporate new related keywords": "incorporateKeywords",
        "Optimize subheadings": "optimizeSubheadings",
        "Optimize your Search Engine Title": "optimizeSearchTitle",
        "Optimize Page Title": "optimizePageTitle",
        "Improve main content": "improveMainContent",
        "Expand your content": "expandContent",
        "Optimize schema markup": "schemaMarkup",
        "Improve page structure": "improvePageStructure"
      };

      const processedLabels = new Set();
      const rebuiltKeywordUpdates = [];

      const sortedOriginal = [...originalKeywordUpdates].sort(
        (a, b) => (a.index || 0) - (b.index || 0)
      );

      sortedOriginal.forEach((originalUpdate) => {
        const label = originalUpdate.section_label;
        const sectionKey = sectionKeyByLabel[label];

        // Keep unknown labels as-is
        if (!sectionKey) {
          rebuiltKeywordUpdates.push(JSON.parse(JSON.stringify(originalUpdate)));
          return;
        }

        if (processedLabels.has(label)) {
          return;
        }
        processedLabels.add(label);

        const items = this.keywordSections[sectionKey] || [];
        if (!items.length) {
          // Section removed by user
          return;
        }

        if (label === "Expand your content") {
          const expandPayload = this._buildExpandSectionPayload(
            items,
            originalUpdate
          );

          if (!expandPayload) {
            return;
          }

          const newUpdate = JSON.parse(JSON.stringify(originalUpdate));
          newUpdate.data = expandPayload.sectionData;
          newUpdate.faqData = expandPayload.faqData;
          rebuiltKeywordUpdates.push(newUpdate);
          return;
        }

        let sectionData = [];

        if (label === "Improve page structure") {
          // One string per row, preserving span structure
          items.forEach((item) => {
            const bodyHtml = (item.answer || "").trim();
            const headingText =
              (item.subheadingInfo && item.subheadingInfo.tagText) || "";
            const stripped = this._stripHtmlTags(bodyHtml).trim();

            if (!stripped && !headingText) return;

            let reconstructed = "";

            if (headingText) {
              reconstructed = `<span>${headingText}</span> `;
            }

            const container = document.createElement("div");
            container.innerHTML = bodyHtml;

            if (
              container.childElementCount === 1 &&
              container.firstElementChild.tagName === "P"
            ) {
              reconstructed += (container.firstElementChild.innerHTML || "").trim();
            } else {
              reconstructed += bodyHtml;
            }

            sectionData.push(reconstructed.trim());
          });
        } else {
          // Default: keep each block intact with custom handling for plain text
          items.forEach((item, idx) => {
            let html = (item.answer || "").trim();
            if (!html) return;

            const strippedContent = this._stripHtmlTags(html).trim();
            let hasStructuralHtml = /<(ul|ol|li|table|tr|td|h[1-6]|img|blockquote|pre)\b/i.test(
              html
            );

            if (!strippedContent && !hasStructuralHtml) {
              return;
            }

            const isBareText = !hasStructuralHtml && !/^<\w+/i.test(html);

            if (isBareText) {
              const nextItem = items[idx + 1];
              const nextHtml =
                nextItem && nextItem.answer ? nextItem.answer.trim() : "";
              const nextIsList = /<(ul|ol)\b/i.test(nextHtml);
              const lastIdx = sectionData.length - 1;
              const prevEntry = lastIdx >= 0 ? sectionData[lastIdx] : null;
              const prevIsList =
                prevEntry && /<(ul|ol)\b/i.test(prevEntry);

              if (nextIsList) {
                html = `<ul><li>${strippedContent}</li></ul>`;
                hasStructuralHtml = true;
              } else if (prevIsList) {
                sectionData[lastIdx] = `${prevEntry}<br/><br/>${strippedContent}`;
                return;
              } else {
                html = `<p>${strippedContent}</p>`;
                hasStructuralHtml = true;
              }
            }

            sectionData.push(html);
          });
        }

        if (!sectionData.length) {
          return;
        }

        const newUpdate = JSON.parse(JSON.stringify(originalUpdate));
        newUpdate.data = sectionData;
        rebuiltKeywordUpdates.push(newUpdate);
      });

      // Reindex
      rebuiltKeywordUpdates.forEach((u, idx) => {
        u.index = idx;
      });

      updatedData.keywordUpdates = rebuiltKeywordUpdates;

      if (updatedData.recommend_title && this.titleItems.length > 0) {
        const recommendation = updatedData.recommend_title.recommendation || null;
        const rankedAlternatives = Array.isArray(
          updatedData.recommend_title.ranked_alternatives
        )
          ? updatedData.recommend_title.ranked_alternatives
          : [];

        const optionMap = {};
        if (recommendation && recommendation.option) {
          optionMap[recommendation.option] = JSON.parse(
            JSON.stringify(recommendation)
          );
        }
        rankedAlternatives.forEach((opt) => {
          if (opt && opt.option) {
            optionMap[opt.option] = JSON.parse(JSON.stringify(opt));
          }
        });

        this.titleItems.forEach((item) => {
          if (!item || !item._meta || item._meta.kind !== "title_option") {
            return;
          }

          const optionKey = item._meta.option;
          if (!optionKey) {
            return;
          }

          const parsed = this._parseTitleOptionFields(item.answer || "");
          const target = optionMap[optionKey] || { option: optionKey };

          if (parsed.draftTitle) {
            target.draft_title = parsed.draftTitle;
          }
          if (parsed.whyText) {
            target.why = parsed.whyText;
            target.why_strong = parsed.whyText;
          }
          if (parsed.scoreGain) {
            target.score_gain = parsed.scoreGain;
          }

          optionMap[optionKey] = target;
        });

        const letters = ["A", "B", "C", "D", "E"];
        if (recommendation && recommendation.option) {
          const recommended = optionMap[recommendation.option];
          if (recommended) {
            updatedData.recommend_title.recommendation = recommended;
          }
        }

        const newAlternatives = letters
          .map((letter) => {
            if (recommendation && recommendation.option === letter) {
              return null;
            }
            return optionMap[letter] || null;
          })
          .filter((opt) => opt);

        updatedData.recommend_title.ranked_alternatives = newAlternatives;
      }

      // --- Schema: rebuild schema_data from Schema Markup editor ---
      if (
        this.keywordSections.schemaMarkup &&
        this.keywordSections.schemaMarkup.length > 0
      ) {
        const schemaItem = this.keywordSections.schemaMarkup[0];
        const html = schemaItem.answer || "";
        const container = document.createElement("div");
        container.innerHTML = html;

        const ul = container.querySelector("ul");
        if (ul) {
          const newSchemaData = [];
          const liNodes = ul.querySelectorAll("li");

          liNodes.forEach((li) => {
            const text = (li.textContent || "").trim();
            if (!text) return;

            let type = text;
            let count = null;

            const match = text.match(/^(.*)\(count:\s*([^)]+)\)/i);
            if (match) {
              type = match[1].trim();
              const countRaw = match[2].trim();
              if (countRaw.toLowerCase() === "n/a") {
                count = null;
              } else {
                const parsed = parseInt(countRaw, 10);
                count = Number.isNaN(parsed) ? countRaw : parsed;
              }
            }

            newSchemaData.push({
              type,
              count
            });
          });

          if (newSchemaData.length > 0) {
            updatedData.schema_data = newSchemaData;
          }
        }
      }

      // --- Proposed Outline – rebuild outline_data.outline.hierarchy ---
      if (this.outlineItems && this.outlineItems.length > 0) {
        const outlineHtml = this.outlineItems[0].answer || "";
        const container = document.createElement("div");
        container.innerHTML = outlineHtml;

        const topUl = container.querySelector("ul");
        const hierarchy = [];

        if (topUl) {
          Array.from(topUl.children).forEach((li) => {
            if (!(li instanceof HTMLElement) || li.tagName !== "LI") return;

            const liClone = li.cloneNode(true);
            const nestedUl = liClone.querySelector("ul");
            if (nestedUl && nestedUl.parentNode) {
              nestedUl.parentNode.removeChild(nestedUl);
            }

            let h2FullText = (liClone.textContent || "").trim();
            if (!h2FullText) return;

            if (/^H2:/i.test(h2FullText)) {
              h2FullText = h2FullText.replace(/^H2:/i, "").trim();
            }

            let h2Terms = [];
            const h2KwMatch = h2FullText.match(/\(KW\/LSI:\s*([^)]+)\)/i);
            if (h2KwMatch) {
              h2Terms = h2KwMatch[1]
                .split(",")
                .map((t) => t.trim())
                .filter(Boolean);
              h2FullText = h2FullText.replace(h2KwMatch[0], "").trim();
            }

            const h2Obj = {
              text: h2FullText,
              builtin: h2Terms,
              lsi: []
            };

            const h3List = [];
            const nested = li.querySelector("ul");
            if (nested) {
              Array.from(nested.children).forEach((h3li) => {
                if (!(h3li instanceof HTMLElement) || h3li.tagName !== "LI")
                  return;

                let h3FullText = (h3li.textContent || "").trim();
                if (!h3FullText) return;

                if (/^H3:/i.test(h3FullText)) {
                  h3FullText = h3FullText.replace(/^H3:/i, "").trim();
                }

                let h3Terms = [];
                const h3KwMatch = h3FullText.match(/\(KW\/LSI:\s*([^)]+)\)/i);
                if (h3KwMatch) {
                  h3Terms = h3KwMatch[1]
                    .split(",")
                    .map((t) => t.trim())
                    .filter(Boolean);
                  h3FullText = h3FullText.replace(h3KwMatch[0], "").trim();
                }

                const h3Obj = {
                  text: h3FullText,
                  builtin: h3Terms,
                  lsi: []
                };

                h3List.push(h3Obj);
              });
            }

            hierarchy.push({
              h2: h2Obj,
              h3: h3List
            });
          });
        }

        if (hierarchy.length > 0) {
          const baseOutlineData = base.outline_data || {};
          const newOutlineData = JSON.parse(JSON.stringify(baseOutlineData));

          newOutlineData.outline = {
            hierarchy
          };

          updatedData.outline_data = newOutlineData;
        }
      }

      // --- Structured report fields edited by the SEO Word Work / EEAT sections ---
      // Written back verbatim so they persist in edited_data and render in the report.
      updatedData.eatSchemaTypes = this.eatSchemaTypes || [];
      updatedData.old_related_searches = this.old_related_searches || [];
      updatedData.new_related_searches = this.new_related_searches || [];
      updatedData.subHeadingsAddAll = this.subHeadingsAddAll || [];
      updatedData.subHeadingsReduceAll = this.subHeadingsReduceAll || [];
      updatedData.pAddAll = this.pAddAll || [];
      updatedData.pReduceAll = this.pReduceAll || [];
      updatedData.titleReduceAll = this.titleReduceAll || [];
      updatedData.keywordSuggestions =
        this.keywordSuggestions && typeof this.keywordSuggestions === "object"
          ? this.keywordSuggestions
          : {};

      return updatedData;
    },

    async saveChanges() {
      if (this.isSaving) {
        return;
      }

      this.isSaving = true;
      this.saveStatus = "Saving...";

      try {
        const updatedData = this.rebuildUpdatedDataFromSections();

        const payload = {
          siloKeywordId: this.siloKeywordId,
          data: updatedData
        };

        await this.$store.dispatch("saveEditedData", payload);

        this.reportData = updatedData;
        this.buildCustomSections();

        this.saveStatus = "Saved ✓";
        setTimeout(() => {
          this.saveStatus = "";
        }, 3000);
      } catch (error) {
        this.saveStatus = "Save failed ✗";

        if (this.$store.state.reportsError) {
          this.error = this.$store.state.reportsError;
        }

        setTimeout(() => {
          this.saveStatus = "";
        }, 5000);
      } finally {
        this.isSaving = false;
      }
    }
  },

  mounted() {
    this.loadReportsData();
  },

  beforeDestroy() {
    if (this.saveTimeout) {
      clearTimeout(this.saveTimeout);
    }
  }
};
</script>

<style scoped>
.container {
  width: 800px;
  margin: auto;
  h1 {
    font-family: Mulish;
    font-weight: 700;
    font-size: 35px;
    line-height: 130%;
    color: #014751;
  }
}

.report-section {
  margin-bottom: 40px;
}

.section-header {
  font-family: Mulish;
  font-weight: 700;
  font-size: 16px;
  color: #666;
  margin-bottom: 15px;
  padding: 10px 0;
  color: #014751;
}

.section-title {
  font-family: Mulish;
  font-weight: 700;
  font-size: 24px;
  margin: 20px 0 15px 0;
  color: #014751;
}
.sub-section-title{
  font-family: Mulish;
  font-weight: 700;
  font-size: 14px;
  line-height: 130%;
  color: #014751;
  margin-left: 10px;
  }

.keyword-subsection {
  margin-bottom: 30px;
}

.mt-4 {
  margin-top: 2rem;
  padding-top: 20px;
  border-top: 2px solid #e5e5e5;
  text-align: center;
}

.bg-blue-500 {
  background-color: #3b82f6;
}

.hover\:bg-blue-700:hover {
  background-color: #2563eb;
}

.text-white {
  color: white;
}

.font-bold {
  font-weight: bold;
}

.py-2 {
  padding-top: 0.5rem;
  padding-bottom: 0.5rem;
}

.px-4 {
  padding-left: 1rem;
  padding-right: 1rem;
}

.rounded {
  border-radius: 0.25rem;
}

button:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.ml-3 {
  margin-left: 0.75rem;
  font-weight: 600;
  color: #059669;
}

.save-btn {
  background-color: #ff6600;
}

:deep(.expand-faq-list) {
  list-style-type: circle;
  padding-left: 1.5rem;
  margin-top: 0.5rem;
}

:deep(.expand-faq-list li) {
  font-size: 18px;
  line-height: 1.5;
  margin-bottom: 5px;
}
/* In the parent of Result7Paragraph */
:deep(.expand-faq-list) {
  list-style-type: circle !important;
  padding-left: 1.5rem;
}

:deep(.expand-faq-list li) {
  list-style-type: circle !important;
  margin-bottom: 4px;
}
:deep(.expand-faq-list li::before) {
  content: none !important;
}

</style>
